/**
 * Microphone Selector Dialog
 * Allows users to select which microphone to use for voice mode
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface MicrophoneSelectorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onMicrophoneSelected: (deviceId: string) => void;
}

interface AudioDevice {
  deviceId: string;
  label: string;
  groupId: string;
}

export const MicrophoneSelectorDialog: React.FC<MicrophoneSelectorDialogProps> = ({
  isOpen,
  onClose,
  onMicrophoneSelected,
}) => {
  const [devices, setDevices] = useState<AudioDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      loadMicrophones();
    }
  }, [isOpen]);

  const loadMicrophones = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Request permission first to get device labels, then immediately release the stream
      // This is to trigger the browser's permission prompt if not already granted, and to ensure we can show device names
      const permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      permissionStream.getTracks().forEach(track => track.stop());
      
      // Enumerate all audio input devices
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = allDevices
        .filter(device => device.kind === 'audioinput')
        .map(device => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.slice(0, 8)}`,
          groupId: device.groupId,
        }));

      if (audioInputs.length === 0) {
        setError('No microphones detected. Please connect a microphone and try again.');
      } else {
        setDevices(audioInputs);
        // Select the first device by default
        setSelectedDeviceId(audioInputs[0].deviceId);
      }
    } catch (err) {
      console.error('[MicrophoneSelectorDialog] Failed to enumerate devices:', err);
      setError('Failed to load microphones. Please check your microphone permissions.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (selectedDeviceId) {
      onMicrophoneSelected(selectedDeviceId);
    }
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      style={{ zIndex: 20000 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="flex flex-col gap-4 p-6 rounded-lg border border-[var(--color-accent)] bg-[var(--color-secondary)] text-[var(--color-text)] shadow-xl max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-1">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
              <line x1="8" y1="23" x2="16" y2="23" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-1">Select Microphone</h3>
            <p className="text-sm opacity-80">
              Choose which microphone you'd like to use for voice mode.
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--color-accent)] border-t-transparent" />
            </div>
          ) : error ? (
            <div className="p-3 rounded text-sm bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200">
              {error}
            </div>
          ) : (
            <div className="space-y-2">
              <label className="text-sm font-medium">Available Microphones:</label>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {devices.map((device) => (
                  <label
                    key={device.deviceId}
                    className="flex items-center gap-3 p-3 rounded border border-[var(--color-border)] hover:bg-[var(--color-hover)] cursor-pointer transition-colors"
                    style={{
                      backgroundColor: selectedDeviceId === device.deviceId ? 'var(--color-accent-light)' : 'transparent',
                    }}
                  >
                    <input
                      type="radio"
                      name="microphone"
                      value={device.deviceId}
                      checked={selectedDeviceId === device.deviceId}
                      onChange={(e) => setSelectedDeviceId(e.target.value)}
                      className="w-4 h-4 accent-[var(--color-accent)]"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{device.label}</div>
                      {device.deviceId === 'default' && (
                        <div className="text-xs opacity-60">System Default</div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            className="px-4 py-2 text-sm rounded border border-[var(--color-border)] hover:bg-[var(--color-hover)] transition-colors"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 text-sm rounded bg-[var(--color-accent)] text-white hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleConfirm}
            disabled={!selectedDeviceId || loading || !!error}
          >
            Use This Microphone
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MicrophoneSelectorDialog;
